'use client';

import { deleteSuggestionComment } from '@/app/(tourism)/tourism/lib/actions/suggestionActions';
import { deleteTourismComment } from '@/app/(tourism)/tourism/lib/actions/tourismActions';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { AnimatePresence, motion } from 'framer-motion';
import { Trash2 } from 'lucide-react';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../components/ui/tooltip';
import { getUserColor } from '../../../../lib/utils/getUserColor';
import { Comment } from '../../../../types/shared';
import { useTourismItemEvents } from '../../contexts/TourismGlobalEvents';
import { AddCommentForm } from './AddCommentForm';

type Props = {
  itemId: string;
  initialComments: Comment[];
  itemType: 'place' | 'suggestion';
  scrollToCommentId?: string | null;
};

type DeleteAction = (commentId: string) => Promise<void>;
type ChildrenMap = Map<string | null, Comment[]>;
type CommentEventPayload = Comment & { placeId?: string; suggestionId?: string };
type DeleteEventPayload = {
  commentId: string;
  commentIds?: string[];
  placeId?: string;
  suggestionId?: string;
};

/** дерево: сортировка + parentId -> children[] */
function buildCommentTree(comments: Comment[]): { childrenMap: ChildrenMap } {
  const sorted = [...comments].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const map: ChildrenMap = new Map();
  for (const c of sorted) {
    const key = c.parentId ?? null;
    const arr = map.get(key);
    if (arr) arr.push(c);
    else map.set(key, [c]);
  }
  return { childrenMap: map };
}

/** собрать id комментария + всех descendants (для удаления ветки) */
function collectSubtreeIds(childrenMap: ChildrenMap, rootId: string): string[] {
  const ids: string[] = [];
  const stack: string[] = [rootId];

  while (stack.length) {
    const id = stack.pop()!;
    ids.push(id);
    const kids = childrenMap.get(id) ?? [];
    for (const k of kids) stack.push(k.id);
  }
  return ids;
}

const CommentThread = memo(function CommentThread({
  comment,
  childrenMap,
  itemType,
  itemId,
  deleteAction,
  onCommentAdded,
  getSubtreeIds,
  onOptimisticDeleteIds,
}: {
  comment: Comment;
  childrenMap: ChildrenMap;
  itemType: 'place' | 'suggestion';
  itemId: string;
  deleteAction: DeleteAction;
  onCommentAdded: (newComment: Comment) => void;
  getSubtreeIds: (rootId: string) => string[];
  onOptimisticDeleteIds: (ids: string[]) => void;
}) {
  const [isReplying, setIsReplying] = useState(false);
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === 'ADMIN';

  const replies = childrenMap.get(comment.id) ?? [];
  const userNameColorStyle = getUserColor(comment.user.id);

  const timeAgo = useMemo(
    () =>
      formatDistanceToNow(new Date(comment.createdAt), {
        addSuffix: true,
        locale: ru,
      }),
    [comment.createdAt],
  );

  const handleDelete = useCallback(() => {
    toast(
      (t) => (
        <div className="flex flex-col gap-2">
          <p className="font-semibold">Удалить комментарий?</p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                toast.dismiss(t.id);

                // ✅ оптимистично удаляем ветку
                const ids = getSubtreeIds(comment.id);
                onOptimisticDeleteIds(ids);

                toast.promise(deleteAction(comment.id), {
                  loading: 'Удаление...',
                  success: 'Удалено',
                  error: 'Ошибка',
                });
              }}
              className="px-3 py-1 bg-red-500 text-white rounded text-sm"
            >
              Да, удалить
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-3 py-1 bg-slate-200 text-black rounded text-sm"
            >
              Отмена
            </button>
          </div>
        </div>
      ),
      { duration: 5000 },
    );
  }, [comment.id, deleteAction, getSubtreeIds, onOptimisticDeleteIds]);

  return (
    <article
      id={`comment-${comment.id}`}
      itemScope
      itemType="https://schema.org/Comment"
      className="flex items-start gap-2 sm:gap-3"
    >
      <Image
        src={comment.user.image || '/default-avatar.jpg'}
        alt={`Аватар пользователя ${comment.user.name || 'Аноним'}`}
        width={40}
        height={40}
        className="rounded-full w-8 h-8 sm:w-10 sm:h-10 mt-1 flex-shrink-0"
      />

      <div className="flex-1">
        <div className="bg-slate-800 rounded-lg p-2 sm:p-3">
          <p
            itemProp="author"
            itemScope
            itemType="https://schema.org/Person"
            className="font-bold text-sm sm:text-base"
          >
            <span itemProp="name" style={userNameColorStyle}>
              {comment.user.name || 'Аноним'}
            </span>
          </p>

          <p
            className="text-slate-200 dark:text-slate-200 whitespace-pre-wrap text-sm sm:text-base"
            itemProp="text"
          >
            {comment.text}
          </p>
        </div>

        <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 flex items-center gap-2 sm:gap-3 mt-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <time dateTime={comment.createdAt} itemProp="dateCreated" className="cursor-help">
                {timeAgo}
              </time>
            </TooltipTrigger>
            <TooltipContent>{new Date(comment.createdAt).toLocaleString('ru-RU')}</TooltipContent>
          </Tooltip>

          {session?.user && (
            <button
              onClick={() => setIsReplying((p) => !p)}
              className="font-semibold hover:underline"
              aria-expanded={isReplying}
              aria-controls={`reply-form-${comment.id}`}
            >
              {isReplying ? 'Отмена' : 'Ответить'}
            </button>
          )}

          {isAdmin && (
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 font-semibold text-red-600/80 hover:text-red-600 dark:text-red-500/70 dark:hover:text-red-500"
            >
              <Trash2 size={14} aria-hidden="true" /> Удалить
            </button>
          )}
        </div>

        <AnimatePresence>
          {isReplying && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-2"
              id={`reply-form-${comment.id}`}
            >
              <AddCommentForm
                itemId={itemId}
                itemType={itemType}
                parentId={comment.id}
                onCommentAdded={(newComment) => {
                  setIsReplying(false);
                  onCommentAdded(newComment);
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {replies.length > 0 && (
          <div className="mt-3 space-y-3 pl-3 sm:pl-4 border-l-2 border-slate-300 dark:border-slate-700">
            {replies.map((reply) => (
              <CommentThread
                key={reply.id}
                comment={reply}
                childrenMap={childrenMap}
                itemType={itemType}
                itemId={itemId}
                deleteAction={deleteAction}
                onCommentAdded={onCommentAdded}
                getSubtreeIds={getSubtreeIds}
                onOptimisticDeleteIds={onOptimisticDeleteIds}
              />
            ))}
          </div>
        )}
      </div>
    </article>
  );
});

export function UniversalCommentsSection({
  itemId,
  initialComments,
  itemType,
  scrollToCommentId,
}: Props) {
  const { data: session } = useSession();

  // ✅ локальный источник истины для UI
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [newCommentId, setNewCommentId] = useState<string | null>(null);

  // ✅ SSE только для этого item
  const ev = useTourismItemEvents(itemId);

  // если initialComments поменялись (например, перешли на другой item)
  useEffect(() => {
    setComments(initialComments);
    setNewCommentId(null);
  }, [initialComments]);

  const deleteCommentAction = itemType === 'place' ? deleteTourismComment : deleteSuggestionComment;
  const safeDeleteAction: DeleteAction = async (commentId) => {
    const res = await deleteCommentAction(commentId);
    if (!res?.success) throw new Error('Не удалось удалить комментарий');
  };

  // дерево
  const { childrenMap } = useMemo(() => buildCommentTree(comments), [comments]);
  const topLevelComments = useMemo(() => childrenMap.get(null) ?? [], [childrenMap]);

  const getSubtreeIdsCb = useCallback(
    (rootId: string) => collectSubtreeIds(childrenMap, rootId),
    [childrenMap],
  );

  // const removeByIds = useCallback((ids: string[]) => {
  //   if (!ids.length) return;
  //   const set = new Set(ids);
  //   setComments((cur) => cur.filter((c) => !set.has(c.id)));
  // }, []);

  const idsRef = useRef<Set<string>>(new Set(initialComments.map((c) => c.id)));

  useEffect(() => {
    idsRef.current = new Set(initialComments.map((c) => c.id));
    setComments(initialComments);
    setNewCommentId(null);
  }, [initialComments]);

  const addIfMissing = useCallback((c: Comment) => {
    setComments((cur) => {
      if (idsRef.current.has(c.id)) return cur;
      idsRef.current.add(c.id);
      return [...cur, c];
    });
  }, []);

  const removeByIds = useCallback((ids: string[]) => {
    if (!ids.length) return;
    const set = new Set(ids);
    for (const id of set) idsRef.current.delete(id);

    setComments((cur) => cur.filter((c) => !set.has(c.id)));
  }, []);

  // ✅ SSE -> синхронизация списка
  useEffect(() => {
    if (!ev) return;

    // доп. защита (хотя store уже фильтрует по itemId)
    const belongsToThisItem = (p: { placeId?: string; suggestionId?: string }) => {
      if (p.placeId) return p.placeId === itemId;
      if (p.suggestionId) return p.suggestionId === itemId;
      return true; // если вдруг payload без id (редко) — не блокируем
    };

    if (ev.type === 'NEW_COMMENT') {
      const p = ev.payload as CommentEventPayload;
      if (!belongsToThisItem(p)) return;

      const incoming: Comment = {
        id: p.id,
        text: p.text,
        createdAt: p.createdAt,
        parentId: p.parentId ?? null,
        user: p.user,
      };

      addIfMissing(incoming);
      return;
    }

    if (ev.type === 'DELETE_COMMENT') {
      const p = ev.payload as DeleteEventPayload;
      if (!belongsToThisItem(p)) return;

      // ✅ если сервер прислал commentIds — используем их (идеально)
      if (p.commentIds?.length) {
        removeByIds(p.commentIds);
        return;
      }

      // ✅ fallback: если пришёл только commentId — удалим всю ветку по дереву
      const ids = collectSubtreeIds(childrenMap, p.commentId);
      removeByIds(ids.length ? ids : [p.commentId]);
    }
  }, [ev, itemId, addIfMissing, removeByIds, childrenMap]);

  // scroll/highlight
  useEffect(() => {
    const targetId = newCommentId || scrollToCommentId;
    if (!targetId) return;

    const element = document.getElementById(`comment-${targetId}`);
    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.classList.add('highlight-comment');

    const t = window.setTimeout(() => element.classList.remove('highlight-comment'), 2000);

    if (newCommentId) setNewCommentId(null);

    return () => window.clearTimeout(t);
  }, [newCommentId, scrollToCommentId, comments]);

  // ✅ optimistic add (и для reply тоже)
  const handleCommentAdded = useCallback(
    (newComment: Comment) => {
      setNewCommentId(newComment.id);
      addIfMissing(newComment);
    },
    [addIfMissing],
  );

  // ✅ optimistic delete ветки (когда админ удаляет)
  const handleOptimisticDeleteIds = useCallback((ids: string[]) => removeByIds(ids), [removeByIds]);

  return (
    <TooltipProvider delayDuration={200}>
      <section
        className="space-y-4"
        aria-labelledby="comments-heading"
        itemScope
        itemType="https://schema.org/UserComments"
      >
        <h3 id="comments-heading" className="font-bold text-lg sm:text-xl text-white">
          Комментарии{' '}
          <span className="text-base text-slate-500 dark:text-slate-400">({comments.length})</span>
        </h3>

        {session?.user && (
          <div className="my-4 sm:my-6">
            <AddCommentForm
              itemId={itemId}
              itemType={itemType}
              onCommentAdded={handleCommentAdded}
            />
          </div>
        )}

        {topLevelComments.length > 0 ? (
          <div className="space-y-4" aria-live="polite">
            {topLevelComments.map((comment) => (
              <CommentThread
                key={comment.id}
                comment={comment}
                childrenMap={childrenMap}
                itemType={itemType}
                itemId={itemId}
                deleteAction={safeDeleteAction}
                onCommentAdded={handleCommentAdded}
                getSubtreeIds={getSubtreeIdsCb}
                onOptimisticDeleteIds={handleOptimisticDeleteIds}
              />
            ))}
          </div>
        ) : (
          <p className="text-center text-slate-500 dark:text-slate-400 py-6">
            Комментариев пока нет.
          </p>
        )}
      </section>
    </TooltipProvider>
  );
}
